import type { Lesson } from "@/core/types";
import { criterion, fromCriteria } from "@/core/check";
import { mitErsatz } from "@/office/makro/pruefung";
import { readRechtschreibung } from "@/office/word/pruefen";
import { MAKRO_KORREKTUR } from "../makros";

export const lektionRechtschreibung: Lesson = {
  id: "rechtschreibung",
  title: "Rechtschreibung und Grammatik",
  summary: "Die Prüfung nutzen, ohne ihr blind zu vertrauen.",
  durationMinutes: 7,
  learningGoals: [
    "Die Rechtschreibprüfung gezielt durchlaufen",
    "Wörter zum Wörterbuch hinzufügen",
    "Die Grenzen der automatischen Prüfung kennen",
  ],
  steps: [
    {
      id: "pruefung-durchlaufen",
      title: "Die Prüfung durchlaufen",
      explanation: [
        "Word markiert Rechtschreibfehler rot und Grammatikfehler blau, während du " +
          "tippst. Ein Rechtsklick auf die markierte Stelle zeigt Vorschläge.",
        "Für ein fertiges Dokument lohnt der vollständige Durchlauf: F7 oder " +
          "„Überprüfen“ → „Rechtschreibung und Grammatik“ geht das Dokument von vorn bis " +
          "hinten durch. So übersiehst du keine Stelle, die beim Schreiben " +
          "weggescrollt ist.",
        "Im Bereich am Rand hast du zu jedem Fund vier Möglichkeiten: den Vorschlag " +
          "übernehmen, „Ignorieren“ für diese eine Stelle, „Alle ignorieren“ für das " +
          "ganze Dokument oder „Hinzufügen“, um das Wort dauerhaft ins Wörterbuch zu " +
          "übernehmen.",
      ],
      keyPoints: [
        "F7 startet den vollständigen Durchlauf.",
        "„Hinzufügen“ wirkt dauerhaft für alle Dokumente – sparsam einsetzen.",
        "Rot ist Rechtschreibung, blau ist Grammatik oder Stil.",
      ],
      task: {
        instruction:
          "Lass die Prüfung einmal vollständig durchlaufen (F7) und bereinige das Dokument, " +
          "bis Word keine Rechtschreibfehler mehr meldet.",
        hint: "F7 drücken und jeden Fund entscheiden: Vorschlag übernehmen, ignorieren oder hinzufügen.",
        macro: MAKRO_KORREKTUR,
        macroNurErsatz: true,
        check: () =>
          mitErsatz(
            readRechtschreibung,
            MAKRO_KORREKTUR,
            (bericht) => ({
              rechtschreibfehler: bericht.zahl("rechtschreibfehler") ?? 0,
              grammatikfehler: bericht.zahl("grammatikfehler") ?? 0,
              fehlerAngezeigt: null,
            }),
            ({ rechtschreibfehler, grammatikfehler }) =>
              fromCriteria(
                [
                  criterion(`Keine offenen Rechtschreibfehler (gemeldet: ${rechtschreibfehler})`, rechtschreibfehler === 0),
                  criterion(`Grammatikhinweise: ${grammatikfehler} – zum Nachsehen, kein Muss`, true),
                ],
                {
                  success: "Die Rechtschreibprüfung meldet nichts mehr. Jetzt noch selbst gegenlesen.",
                  failure: "Word meldet noch Rechtschreibfehler.",
                  hint: "„Ignorieren“ zählt als erledigt – aber nur für Wörter, die wirklich richtig sind.",
                },
              ),
          ),
      },
    },
    {
      id: "grenzen",
      title: "Was die Prüfung nicht kann",
      explanation: [
        "Die Rechtschreibprüfung vergleicht Wörter mit einem Wörterbuch. Steht ein Wort " +
          "darin, ist es für Word richtig – auch wenn es an dieser Stelle falsch ist.",
        "„Wir haben die Rechnung an Sie gesandt“ und „gesendet“ sind beide korrekt " +
          "geschrieben. „Der Vertrag wurde nicht unterschrieben“ und „Der Vertrag wurde " +
          "nun unterschrieben“ ebenso – mit sehr unterschiedlicher Bedeutung. Solche " +
          "Fehler findet nur ein Mensch.",
        "Praktischer Rat: Lass Word laufen, aber lies danach selbst. Besonders wirksam " +
          "ist es, den Text laut zu lesen oder ihn sich von Word vorlesen zu lassen – " +
          "„Überprüfen“ → „Laut vorlesen“. Beim Hören fallen holprige Sätze auf, über die " +
          "das Auge hinweggleitet.",
      ],
      keyPoints: [
        "Die Prüfung kennt Wörter, nicht Bedeutung.",
        "„Überprüfen“ → „Laut vorlesen“ deckt holprige Sätze auf.",
        "Kein Werkzeug ersetzt das eigene Gegenlesen.",
      ],
    },
  ],
};
